
import { FeatureGuide } from '@/types/knowledgeBase';

/**
 * Get feature guides specific to SuperAdmin users
 */
export const getSuperAdminFeatureGuides = (): FeatureGuide[] => {
  return [
    {
      id: 'super-admin-dashboard',
      title: 'SuperAdmin Dashboard Overview',
      description: 'Learn how to use the SuperAdmin dashboard to monitor all SaaS instances',
      content: `
        <h2>SuperAdmin Dashboard</h2>
        <p>The SuperAdmin Dashboard provides a high-level overview of all SaaS instances in your platform.</p>
        
        <h3>Key Dashboard Features</h3>
        <ul>
          <li><strong>Instance Stats:</strong> View total, active, and suspended instances at a glance</li>
          <li><strong>License Verification:</strong> Verify your license status and limits</li>
          <li><strong>Instance Management:</strong> Quick access to manage all your SaaS instances</li>
        </ul>
        
        <h3>How to Use the Dashboard</h3>
        <p>The dashboard is divided into three main sections:</p>
        <ol>
          <li><strong>License Verification Panel:</strong> Displays your current license status, including validity, instance limits, and plan details. Use the "Verify License" button to refresh this information.</li>
          <li><strong>Statistics Cards:</strong> Shows key metrics including total instances, active instances, and suspended instances.</li>
          <li><strong>Instance Management Tabs:</strong> Provides tabs to switch between Overview, Active Instances, and Suspended Instances views.</li>
        </ol>
      `,
      category: 'Dashboard',
      lastUpdated: '2025-04-08',
      isNew: true
    },
    {
      id: 'instances-management',
      title: 'Managing SaaS Instances',
      description: 'Learn how to create, suspend, and manage instances',
      content: `
        <h2>Managing SaaS Instances</h2>
        <p>As a SuperAdmin, you have full control over all SaaS instances in your platform.</p>
        
        <h3>Key Instance Management Features</h3>
        <ul>
          <li><strong>Create New Instance:</strong> Set up new customer instances</li>
          <li><strong>Suspend/Activate:</strong> Control the active status of any instance</li>
          <li><strong>Instance Details:</strong> View and modify settings for each instance</li>
        </ul>
        
        <h3>How to Create a New Instance</h3>
        <ol>
          <li>Navigate to the Instances page from the top navigation</li>
          <li>Click the "Create New Instance" button</li>
          <li>Fill in the instance details including name, domain, and plan</li>
          <li>Click "Create" to set up the new instance</li>
        </ol>
        
        <h3>Managing Instance Status</h3>
        <p>To suspend an instance:</p>
        <ol>
          <li>Find the instance in the instances list</li>
          <li>Click the "Suspend" button for that instance</li>
          <li>Confirm the suspension in the dialog</li>
        </ol>
        
        <p>To reactivate a suspended instance, follow the same steps but click "Activate" instead.</p>
      `,
      category: 'Instances',
      lastUpdated: '2025-04-08',
      isNew: false
    },
    {
      id: 'subscription-management',
      title: 'Subscription Management',
      description: 'How to manage subscription plans and customer billing',
      content: `
        <h2>Subscription Management</h2>
        <p>The Subscriptions section allows you to manage all customer subscription plans and billing.</p>
        
        <h3>Key Subscription Features</h3>
        <ul>
          <li><strong>Subscription Plans:</strong> Create and manage available plans</li>
          <li><strong>Customer Billing:</strong> View and manage billing information</li>
          <li><strong>Payment History:</strong> Track all payment transactions</li>
        </ul>
        
        <h3>Managing Subscription Plans</h3>
        <ol>
          <li>Navigate to Plans from the top navigation</li>
          <li>Create new plans with the "Add Plan" button</li>
          <li>Edit existing plans by clicking on the plan name</li>
          <li>Set pricing, features, and availability for each plan</li>
        </ol>
        
        <h3>Viewing Customer Subscriptions</h3>
        <p>To view all customer subscriptions:</p>
        <ol>
          <li>Navigate to Subscriptions from the top navigation</li>
          <li>Use the search and filter tools to find specific customers</li>
          <li>Click on any subscription to view detailed information</li>
        </ol>
      `,
      category: 'Subscriptions',
      lastUpdated: '2025-04-07',
      isNew: false
    },
    {
      id: 'system-settings',
      title: 'System Settings Configuration',
      description: 'Configure global settings for your SaaS platform',
      content: `
        <h2>System Settings Configuration</h2>
        <p>The Settings section allows you to configure global system settings for your entire SaaS platform.</p>
        
        <h3>Available Settings Categories</h3>
        <ul>
          <li><strong>System:</strong> Core platform settings</li>
          <li><strong>Database:</strong> Database connection and configuration</li>
          <li><strong>Server:</strong> Server-side settings and performance</li>
          <li><strong>Domain:</strong> Domain management and routing</li>
          <li><strong>Payments:</strong> Payment gateway configurations</li>
          <li><strong>Integrations:</strong> Third-party service integrations</li>
          <li><strong>Business Plans:</strong> Default business signup plans</li>
        </ul>
        
        <h3>How to Update Settings</h3>
        <ol>
          <li>Navigate to Settings from the top navigation</li>
          <li>Select the appropriate tab for the settings you want to modify</li>
          <li>Make your changes to the settings form</li>
          <li>Click "Save Changes" to apply your modifications</li>
        </ol>
        
        <h3>Important Considerations</h3>
        <p>When modifying system settings:</p>
        <ul>
          <li>Changes to Database settings may require system restart</li>
          <li>Domain settings affect all customer URLs</li>
          <li>Payment gateway changes can affect billing for all customers</li>
        </ul>
      `,
      category: 'Settings',
      lastUpdated: '2025-04-05',
      isNew: false
    },
    {
      id: 'license-verification',
      title: 'License Management',
      description: 'Verify and manage your SuperAdmin license',
      content: `
        <h2>License Management</h2>
        <p>SuperAdmin licenses control your ability to create and manage SaaS instances.</p>
        
        <h3>Key License Features</h3>
        <ul>
          <li><strong>License Verification:</strong> Check license validity</li>
          <li><strong>Instance Limits:</strong> View allowed and used instances</li>
          <li><strong>Plan Details:</strong> See your current plan features</li>
        </ul>
        
        <h3>Verifying Your License</h3>
        <ol>
          <li>Navigate to the Dashboard</li>
          <li>Locate the License Verification panel</li>
          <li>Click "Verify License" to check current status</li>
        </ol>
        
        <h3>Understanding License Limitations</h3>
        <p>Your license includes specific limitations:</p>
        <ul>
          <li><strong>Maximum Instances:</strong> The total number of instances you can create</li>
          <li><strong>Used Instances:</strong> The number of instances currently in use</li>
          <li><strong>Remaining Instances:</strong> How many more instances you can create</li>
        </ul>
        
        <h3>Upgrading Your License</h3>
        <p>If you need to increase your instance limits:</p>
        <ol>
          <li>Contact your license provider</li>
          <li>Request an upgrade to your current plan</li>
          <li>Apply the new license key when provided</li>
        </ol>
      `,
      category: 'License',
      lastUpdated: '2025-04-06',
      isNew: true
    }
  ];
};
